import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Flex,
  Spacer,
  useToast,
} from "@chakra-ui/react";

// Import step components
import SelectProductType from "./product-registration/SelectProductType";
import ProductRegistration from "./product-registration/ProductRegistration";
import RegistrationComplete from "./product-registration/RegistrationComplete";
import DisplayProductContinue from "./product-registration/DisplayProductContinue";
import ProductSelection from "./product-registration/ProductSelection";
import { registerProduct, getRegistrationErrorMessage } from "../services/api";

const REGISTRATION_STAGE = {
  SELECT_PRODUCT: "SELECT_PRODUCT",
  SELECT_SPECIFIC_PRODUCT: "SELECT_SPECIFIC_PRODUCT",
  PRODUCT_REGISTRATION: "PRODUCT_REGISTRATION",
  REGISTRATION_COMPLETE: "REGISTRATION_COMPLETE",
  DISPLAY_CONTINUE: "DISPLAY_CONTINUE",
};

// Format a date to DD/MM/YY string
const formatDate = (date) => {
  if (!date) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};

function AddProductDialog({ isOpen, onClose, onAddProduct }) {
  const [currentStep, setCurrentStep] = useState(
    REGISTRATION_STAGE.SELECT_PRODUCT
  );
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const productRegistrationRef = useRef();
  const toast = useToast();

  // Reset dialog state when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(REGISTRATION_STAGE.SELECT_PRODUCT);
      setSelectedProduct(null);
      setRegistrationData(null);
    }
  }, [isOpen]);

  const handleProductTypeSelect = (category) => {
    setSelectedProduct(category);
    setCurrentStep(REGISTRATION_STAGE.SELECT_SPECIFIC_PRODUCT);
  };

  const handleSpecificProductSelect = (product) => {
    setSelectedProduct(product);
    if (product.type === "Display") {
      setCurrentStep(REGISTRATION_STAGE.DISPLAY_CONTINUE);
    } else {
      setCurrentStep(REGISTRATION_STAGE.PRODUCT_REGISTRATION);
    }
  };

  const handleProductRegistration = async (data) => {
    const formattedData = {
      ...selectedProduct,
      ...data,
      purchaseDate: data.purchaseDate ? formatDate(data.purchaseDate) : "",
      registrationDate: formatDate(new Date()),
    };

    setIsRegistering(true);
    try {
      const response = await registerProduct(formattedData);
      setRegistrationData({ ...formattedData, registrationId: response.registrationId });
      setCurrentStep(REGISTRATION_STAGE.REGISTRATION_COMPLETE);
      
      // Show success toast
      toast({
        title: "Product Registered",
        description: response.message || "Your product has been successfully registered.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Registration Failed",
        description: getRegistrationErrorMessage(error),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const handleRegistrationComplete = () => {
    onAddProduct(registrationData);
    onClose();
  };

  const handleBack = () => {
    if (currentStep === REGISTRATION_STAGE.SELECT_SPECIFIC_PRODUCT) {
      setCurrentStep(REGISTRATION_STAGE.SELECT_PRODUCT);
      setSelectedProduct(null);
    } else if (currentStep === REGISTRATION_STAGE.PRODUCT_REGISTRATION) {
      setCurrentStep(REGISTRATION_STAGE.SELECT_SPECIFIC_PRODUCT);
    } else if (currentStep === REGISTRATION_STAGE.REGISTRATION_COMPLETE) {
      setCurrentStep(REGISTRATION_STAGE.PRODUCT_REGISTRATION);
    }
  };

  const handleClose = () => {
    setCurrentStep(REGISTRATION_STAGE.SELECT_PRODUCT);
    setSelectedProduct(null);
    setRegistrationData(null);
    onClose();
  };

  const handleRegisterClick = () => {
    if (productRegistrationRef.current) {
      const success = productRegistrationRef.current.submit();
      if (success) {
        // The onSubmit callback will handle the state transition
        // No need to do anything here as handleProductRegistration will be called
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="6xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader></ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <Box>
            {currentStep === REGISTRATION_STAGE.SELECT_PRODUCT && (
              <SelectProductType
                onSelect={handleProductTypeSelect}
              />
            )}

            {currentStep === REGISTRATION_STAGE.SELECT_SPECIFIC_PRODUCT && selectedProduct && (
              <ProductSelection
                category={selectedProduct}
                onSelect={handleSpecificProductSelect}
                onBack={handleBack}
              />
            )}

            {currentStep === REGISTRATION_STAGE.PRODUCT_REGISTRATION && selectedProduct && (
              <ProductRegistration
                ref={productRegistrationRef}
                product={selectedProduct}
                onSubmit={handleProductRegistration}
              />
            )}

            {currentStep === REGISTRATION_STAGE.REGISTRATION_COMPLETE &&
              registrationData && (
                <RegistrationComplete
                  registrationData={registrationData}
                  onComplete={handleRegistrationComplete}
                />
              )}

            {currentStep === REGISTRATION_STAGE.DISPLAY_CONTINUE &&
              selectedProduct?.type === "Display" && <DisplayProductContinue />}
          </Box>
        </ModalBody>

        <ModalFooter>
          <Flex w="100%" align="center">
            {currentStep === REGISTRATION_STAGE.SELECT_PRODUCT && (
              <>
                <Button
                  colorScheme="purple"
                  size="lg"
                  minW="120px"
                  onClick={handleClose}
                >
                  Close
                </Button>
              </>
            )}

            {currentStep === REGISTRATION_STAGE.SELECT_SPECIFIC_PRODUCT && (
              <>
                <Button
                  colorScheme="purple"
                  size="lg"
                  minW="120px"
                  onClick={handleBack}
                >
                  Back
                </Button>
              </>
            )}
            
            {currentStep === REGISTRATION_STAGE.PRODUCT_REGISTRATION && (
              <>
                <Button
                  colorScheme="purple"
                  size="lg"
                  minW="120px"
                  onClick={handleBack}
                >
                  Back
                </Button>
                <Spacer />
                <Button
                  colorScheme="green"
                  size="lg"
                  minW="120px"
                  onClick={handleRegisterClick}
                  isLoading={isRegistering}
                  loadingText="Registering..."
                >
                  Register Product
                </Button>
              </>
            )}

            {currentStep === REGISTRATION_STAGE.REGISTRATION_COMPLETE && (
              <>
                <Spacer />
                <Button
                  colorScheme="green"
                  size="lg"
                  minW="120px"
                  onClick={handleRegistrationComplete}
                >
                  Close
                </Button>
              </>
            )}

            {currentStep === REGISTRATION_STAGE.DISPLAY_CONTINUE && (
              <Button
                colorScheme="purple"
                size="lg"
                minW="120px"
                onClick={handleClose}
              >
                Close
              </Button>
            )}
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

AddProductDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddProduct: PropTypes.func.isRequired,
};

export default AddProductDialog;
